<script setup lang="ts">
/**
 * Accessible confirmation dialog (teleported to <body>). Presentational and
 * reusable: the parent owns the open state and the copy, and reacts to the
 * `confirm` / `cancel` events. Closes on Escape or backdrop click (unless
 * `loading`). SSR-safe: nothing is rendered while closed.
 */
import { watch, onBeforeUnmount } from 'vue'

const props = withDefaults(
  defineProps<{
    open: boolean
    title: string
    message: string
    confirmLabel: string
    cancelLabel: string
    loading?: boolean
    danger?: boolean
  }>(),
  { loading: false, danger: false },
)

const emit = defineEmits<{ confirm: []; cancel: [] }>()

const titleId = `confirm-dialog-${Math.random().toString(36).slice(2, 8)}`

function cancel() {
  if (!props.loading) emit('cancel')
}
function confirm() {
  if (!props.loading) emit('confirm')
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') cancel()
}

watch(
  () => props.open,
  (open) => {
    if (typeof document === 'undefined') return
    if (open) document.addEventListener('keydown', onKeydown)
    else document.removeEventListener('keydown', onKeydown)
  },
)
onBeforeUnmount(() => {
  if (typeof document !== 'undefined') document.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="cd-overlay" @click.self="cancel">
      <div class="cd" role="dialog" aria-modal="true" :aria-labelledby="titleId">
        <h2 :id="titleId" class="cd__title">{{ title }}</h2>
        <p class="cd__message">{{ message }}</p>
        <div class="cd__actions">
          <button type="button" class="cd__btn cd__btn--ghost" :disabled="loading" @click="cancel">
            {{ cancelLabel }}
          </button>
          <button
            type="button"
            class="cd__btn"
            :class="danger ? 'cd__btn--danger' : 'cd__btn--dark'"
            :disabled="loading"
            @click="confirm"
          >
            {{ confirmLabel }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.cd-overlay {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal, 70);
  display: grid;
  place-items: center;
  padding: var(--layout-gutter);
  background: oklch(20% 0.02 345 / 0.45);
}
.cd {
  width: 100%;
  max-width: 26rem;
  padding: clamp(1.25rem, 3vw, 1.75rem);
  background: var(--color-white);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.cd__title { font-size: var(--text-lg); font-weight: 800; color: var(--color-chocolate); }
.cd__message { font-size: var(--text-sm); color: var(--text-muted); line-height: var(--leading-normal); }
.cd__actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 0.6rem; margin-top: 0.4rem; }
.cd__btn {
  padding: 0.6rem 1.2rem;
  border: 0;
  border-radius: var(--radius-pill);
  font-size: var(--text-sm);
  font-weight: 600;
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-out), opacity var(--dur-fast);
}
.cd__btn:disabled { opacity: 0.5; cursor: default; }
.cd__btn--dark { background: var(--color-chocolate); color: var(--color-white); }
.cd__btn--dark:hover:not(:disabled) { background: color-mix(in oklab, var(--color-chocolate) 85%, white); }
.cd__btn--danger { background: var(--color-cherry); color: var(--color-white); }
.cd__btn--danger:hover:not(:disabled) { background: color-mix(in oklab, var(--color-cherry) 88%, white); }
.cd__btn--ghost { background: transparent; color: var(--color-chocolate); border: 1px solid var(--border-strong); }
.cd__btn--ghost:hover:not(:disabled) { border-color: var(--primary); }
</style>
